import * as React from 'react'
import { cn } from '@/lib/utils'

export interface CardProps extends React.HTMLAttributes<HTMLElement> {
  as?: keyof JSX.IntrinsicElements
}

const Card = React.forwardRef<HTMLElement, CardProps>(
  ({ className, as: Component = 'div', ...props }, ref) => {
    const TagComponent = Component as React.ElementType
    return (
      <TagComponent
        ref={ref}
        className={cn(
          'card w-[440px] overflow-hidden rounded-xl bg-white text-txt-primary shadow-modal-primary',
          className,
        )}
        {...(props as any)}
      />
    )
  },
) as (<T extends keyof JSX.IntrinsicElements>(
  props: CardProps & JSX.IntrinsicElements[T],
) => React.ReactElement) & { displayName: string }

Card.displayName = 'Card'

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('card-header  flex flex-col gap-y-7 px-8  py-8', className)}
    {...props}
  />
))
CardHeader.displayName = 'CardHeader'

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3 ref={ref} className={cn('card-title font-bold', className)} {...props} />
))
CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-txt-primary', className)} {...props} />
))
CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'first:pt-3--- [&:not(:last-child)]:pb-3--- dialog-content [&:not(:last-child)]:pt-3--- px-8 py-0 first:pt-8 last:pb-8',
      className,
    )}
    {...props}
  />
))
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'card-footer  flex flex-col gap-y-2 px-8 pb-8 pt-6 text-sm text-txt-secondary',
      className,
    )}
    {...props}
  />
))
CardFooter.displayName = 'CardFooter'

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
